import { IUploadInfoDict, ITrkImgData } from '@/../typings';
import { filestore } from '@/filestore';
import { store } from '@/store';
import {
	ILoadedMediaTypes,
	IOnSetCurrentUploads,
	onAddToast,
	onSetCurrentUploads
} from '@/store/actions';
import { zwClient } from '@/sync/zapworks';
import { ALLOWED_UPLOAD_IMAGE_TYPES, MAX_AUDIO_SIZE, MAX_IMAGE_SIZE, MAX_MODEL_SIZE, MAX_VIDEO_SIZE } from '@/utils';
import { ToastsData } from '@/utils/toasts-data';
import { IFileAudio, IFileImage, IFileImage360, IFileModel3D, IFileStreamingVideo, IFileVideo360, IFileZPT, IJobProgress, IProgressCallback, IProgressStatus } from '@zappar/zw-api-client';
import { Dispatch } from 'redux';
import { MathUtils } from 'three';
import { IOnProgressCb, IOnCompletionCb, IOnErrorCb, IUploadFileTypes, IOnErrorData, IOnProgressData, IOnCompletionData, IErrorText, IUploadFilesOfType, IUploadFilesOfMultipleTypes, IUploadResponseFileTypes, ICreateUploadIndexToFileDict, IUploadMedia } from '../specs';

export interface IUploadIndexToFileDict {
	[index: string]: {
		file: File;
		id: string;
	};
}

export const uploadResponseHandler = (args: {
	onProgress: IOnProgressCb;
	onCompletion: IOnCompletionCb;
	onError: IOnErrorCb;
	progressData: IJobProgress;
	perc: number;
	fileInfo: { id: string; file: File };
	fileType: ILoadedMediaTypes;
}) => {
	const {
		onProgress,
		onCompletion,
		onError,
		progressData: { fileInfo: data, errorCode, status: progressStatus, desc },
		perc,
		fileInfo: {
			id,
			file: { name: fileName },
		},
		fileType,
	} = args;

	const baseProgressData = { id, fileName };

	switch (progressStatus) {
		case IProgressStatus.uploading:
			if (!onProgress) return;
			onProgress({ ...baseProgressData, type: IProgressStatus.uploading, perc });
			if (perc !== 100) return;
			onProgress({
				...baseProgressData,
				type: IProgressStatus.processing,
				perc: 0,
			});
			break;
		case IProgressStatus.processing:
			if (!onProgress) return;
			onProgress({
				...baseProgressData,
				type: IProgressStatus.processing,
				perc,
			});
			break;
		case IProgressStatus.completed:
			if (!onCompletion) return;
			if (!data) return;
			onCompletion({
				...baseProgressData,
				data,
			});
			break;
		case IProgressStatus.error:
			if (onError) {
				onError({
					...baseProgressData,
					status: errorCode,
					desc: desc || '',
					type: IProgressStatus.error,
					mediaType: fileType,
				});
			}
			break;
		default:
			break;
	}
};

export const checkAndFilterFilesByType = (args: { files: File[]; allowedFileTypes: string[]; onError: IOnErrorCb; fileType: ILoadedMediaTypes, is360Content?: boolean }) => {
	const { files, allowedFileTypes, onError, fileType, is360Content } = args;
	//convert filelist to file array
	const allFileArray: File[] = [];
	for (let i = 0; i < files.length; i++) {
		// if unknown file type use extension instead
		const fileApiType = files[i].type || `.${files[i].name.split('.').pop()}`;

		const isImage = ALLOWED_UPLOAD_IMAGE_TYPES.includes(fileApiType as IUploadFileTypes)

		if (allowedFileTypes.includes(IUploadFileTypes.video_all) && !isImage) {
			if (fileApiType.split('/')[0] === 'video') {
				allFileArray.push(files[i]);
				continue;
			} else if (onError) {
				onError({
					id: MathUtils.generateUUID(),
					fileName: files[i].name,
					desc: IErrorText.formatNotSupported,
					type: IProgressStatus.error,
					mediaType: is360Content ? ILoadedMediaTypes.background360 : fileType,
				});
				continue;
			}
		}
		if (!allowedFileTypes.includes(fileApiType)) {
			if (onError) {
				onError({
					id: MathUtils.generateUUID(),
					fileName: files[i].name,
					desc: fileApiType.includes('gltf') ? IErrorText.gltfMediaLibrary : IErrorText.formatNotSupported,
					type: IProgressStatus.error,
					mediaType: fileType,
				});
			}
		} else allFileArray.push(files[i]);
	}
	const duplicateFileNames = allFileArray
		.map((file) => file.name)
		.reduce((acc, v, i, arr) => (arr.indexOf(v) !== i && acc.indexOf(v) === -1 ? acc.concat(v) : acc), [] as string[])
		.map((fileName: string) => {
			if (onError) {
				onError({
					id: MathUtils.generateUUID(),
					fileName,
					desc: IErrorText.fileDuplicate,
					type: IProgressStatus.error,
					mediaType: fileType,
				});
			}
			return fileName;
		});

	const allFilteredFiles = allFileArray.filter((file) => !duplicateFileNames.includes(file.name));
	return allFilteredFiles;
};

export const returnFileType = (mediaType: IUploadFileTypes) => {
	switch (mediaType) {
		case IUploadFileTypes.model_zip:
		case IUploadFileTypes.model_glb:
		case IUploadFileTypes.model_gltf:
		case IUploadFileTypes.model_glb_ext:
			return ILoadedMediaTypes.model3d;
		case IUploadFileTypes.image_png:
		case IUploadFileTypes.image_jpeg:
			return ILoadedMediaTypes.image;
		case IUploadFileTypes.video_quicktime:
		case IUploadFileTypes.video_mpeg:
		case IUploadFileTypes.video_mp4:
		case IUploadFileTypes.video_avi:
		case IUploadFileTypes.video_webm:
		case IUploadFileTypes.video_3gp:
		case IUploadFileTypes.video_flv:
		case IUploadFileTypes.video_all:
			return ILoadedMediaTypes.video;
		case IUploadFileTypes.audio_wav:
		case IUploadFileTypes.audio_ogg:
		case IUploadFileTypes.audio_mpeg:
		case IUploadFileTypes.audio_m4a:
			return ILoadedMediaTypes.audio;
		default:
			return null;
	}
};

export const onErrorHandler = (e: IOnErrorData, onSetCurrentUploads: IOnSetCurrentUploads, dispatch: Dispatch<any>) => {
	const { id, fileName, status: errorStatus, desc, mediaType } = e;
	dispatch(
		onSetCurrentUploads(id, {
			errorText: desc || 'error uploading',
			errorStatus,
			fileName,
			mediaType,
			type: IProgressStatus.error,
		})
	);
};

export const onProgressHandler = (p: IOnProgressData, onSetCurrentUploads: IOnSetCurrentUploads, dispatch: Dispatch<any>) => {
	const { id, ...rest } = p;
	dispatch(onSetCurrentUploads(id, rest));
};

export const onCompletionHandler = async (
	c: IOnCompletionData,
	onSetCurrentUploads: IOnSetCurrentUploads,
	mediaType: ILoadedMediaTypes,
	dispatch: Dispatch<any>
) => {
	const {
		id,
		data: { url, id: zmlFileId, caption: name, filename: fileName },
	} = c;

	dispatch(
		onSetCurrentUploads(id, {
			fileName,
			type: IProgressStatus.completed,
			perc: 100,
		})
	);
	setTimeout(() => {
		dispatch(onSetCurrentUploads(id, null));
	}, 2000);

    if (mediaType === ILoadedMediaTypes.audio) {
        filestore.clearAudioCache()
    } else if (mediaType === ILoadedMediaTypes.background360) {
        filestore.clear360FilesCache()
    }
}

export const onSubmitHandler = (fileDict: IUploadIndexToFileDict, onSetCurrentUploads: IOnSetCurrentUploads, dispatch: Dispatch<any>, is360Content = false) => {
	Object.keys(fileDict).forEach((uploadId) => {
		const { file, id } = fileDict[uploadId];
		const loadedMediaType = returnFileType(file.type as IUploadFileTypes);

		if (is360Content) {
			dispatch(onSetCurrentUploads(id, { mediaType: ILoadedMediaTypes.background360 }));
			return;
		} 

		switch (loadedMediaType) {
			case ILoadedMediaTypes.image:
				dispatch(onSetCurrentUploads(id, { mediaType: ILoadedMediaTypes.image }));
				break;
			case ILoadedMediaTypes.video:
				dispatch(onSetCurrentUploads(id, { mediaType: ILoadedMediaTypes.video }));
				break;
			case ILoadedMediaTypes.audio:
				dispatch(onSetCurrentUploads(id, { mediaType: ILoadedMediaTypes.audio }));
				break;
			case ILoadedMediaTypes.model3d:
				dispatch(onSetCurrentUploads(id, { mediaType: ILoadedMediaTypes.model3d }));
				break;
			default:
				break;
		}
	});
};

export const calcImgAspectRatio = (url: string, ratioCb: (ratio: number) => any) => {
	const img = new Image();
	img.addEventListener('load', function () {
		ratioCb(this.naturalWidth / this.naturalHeight);
	});
	img.src = url;
};

export const calcTotalUploadPerc = (numberUploads: number, currentUploads: IUploadInfoDict) => {
	const currentUploadFileNames = Object.keys(currentUploads);
	let totalUploadProgressPerc = 0;
	const adjFactor = numberUploads * 2;
	const numberCompletedUploads = numberUploads - currentUploadFileNames.length;
	totalUploadProgressPerc = (numberCompletedUploads * 2 * 100) / adjFactor;
	// uploads consist of processing & actual upload assumed taking 50:50 time

	currentUploadFileNames.forEach((fln, i) => {
		if (currentUploads[fln]!.type === IProgressStatus.processing || currentUploads[fln]!.type === IProgressStatus.completed || currentUploads[fln]!.type === IProgressStatus.trainingCompleted) {
			totalUploadProgressPerc += 100 / adjFactor; // 100% of loading already done, so added;
		}
		if (currentUploads[fln]?.perc) {
			totalUploadProgressPerc += currentUploads[fln]?.perc ?? 0 / adjFactor;
		}
	});
	return totalUploadProgressPerc <= 100 ? totalUploadProgressPerc : 100;
};

export const imgQualityNumToTxt = (trkImgData: ITrkImgData | null) => {
	if (!trkImgData) return '';
	let txt = 'Poor';
	if (trkImgData?.quality === 2) txt = 'Moderate';
	if (trkImgData?.quality === 3) txt = 'Good';
	return txt;
};

export const setUploadTitle = (numUploads = 0, numErrors: number) => {
	let title = `Uploading ${numUploads > 1 ? numUploads + ' ' : ''} asset${numUploads > 1 ? 's' : ''}...`;
	if (numErrors === 1) title = 'Asset upload failed';
	if (numErrors > 1) title = 'Assets upload failed';

	return title;
};

export const createUploadIndexToFileDict: ICreateUploadIndexToFileDict = ({files, allowedFileTypes, onError, fileType, dispatch, onProgress, is360Content}) => {
	// checkAndFilterFilesByType filters out filename duplicates
	const allowedFiles = checkAndFilterFilesByType({
		files,
		allowedFileTypes,
		onError,
		fileType,
		is360Content
	});

	const imageFilesArray: File[] = [];
	const image360FilesArray: File[] = [];
	const videoFilesArray: File[] = [];
	const video360FilesArray: File[] = [];
	const audioFilesArray: File[] = [];
	const model3dFilesArray: File[] = [];
	const uploadIndexToFileDict: IUploadIndexToFileDict = {};

	for (let i = 0; i < allowedFiles.length; i++) {
		const id = MathUtils.generateUUID();
		const file = allowedFiles[i];

		const fileType = returnFileType((allowedFiles[i].type || `.${files[i].name.split('.').pop()}`) as IUploadFileTypes);
		switch (fileType) {
			case ILoadedMediaTypes.model3d: {
				if (file.size > MAX_MODEL_SIZE) {
					dispatch(onAddToast(ToastsData.MaxUploadSizeExceededError(MAX_MODEL_SIZE / 1000000, 'model')));
					continue;
				}
				model3dFilesArray.push(file);
				uploadIndexToFileDict[`model3d_${model3dFilesArray.length - 1}`] = {
					id,
					file,
				};
				break;
			}
			case ILoadedMediaTypes.image: {
				if (file.size > MAX_IMAGE_SIZE) {
					// larger than 10 MB
					dispatch(onAddToast(ToastsData.MaxUploadSizeExceededError(MAX_IMAGE_SIZE / 1000000, 'image')));
					continue;
				}

				if (is360Content) {
					image360FilesArray.push(file)
					uploadIndexToFileDict[`image360_${image360FilesArray.length - 1}`] = {id,file};
				} else {
					imageFilesArray.push(file);
					uploadIndexToFileDict[`image_${imageFilesArray.length - 1}`] = {id,file};
				}
				break;
			}
			case ILoadedMediaTypes.video: {
				if (file.size > MAX_VIDEO_SIZE) {
					// larger than 200 MB
					dispatch(onAddToast(ToastsData.MaxUploadSizeExceededError(MAX_VIDEO_SIZE / 1000000, 'video')));
					continue;
				}

				if (is360Content) {
					video360FilesArray.push(file);
					uploadIndexToFileDict[`video360_${video360FilesArray.length - 1}`] = {id,file};
				} else {
					videoFilesArray.push(file);
					uploadIndexToFileDict[`video_${videoFilesArray.length - 1}`] = {id,file};
				}
				break;
			}
			case ILoadedMediaTypes.audio: {
				if (file.size > MAX_AUDIO_SIZE) {
					// larger than 50 MB
					dispatch(onAddToast(ToastsData.MaxUploadSizeExceededError(MAX_AUDIO_SIZE / 1000000, 'audio')));
					continue;
				}
				audioFilesArray.push(file);
				uploadIndexToFileDict[`audio_${audioFilesArray.length - 1}`] = {
					id,
					file,
				};
				break;
			}
			default:
				break;
		}
		if (!onProgress) continue;
		onProgress({
			id,
			fileName: file.name,
			type: IProgressStatus.uploading,
			perc: 0,
		});
	}
	return { uploadIndexToFileDict, imageFilesArray, videoFilesArray, audioFilesArray, model3dFilesArray, image360FilesArray, video360FilesArray };
};

export const uploadMedia: IUploadMedia = async ({
	uploadIndexToFileDict,
	imageFilesArray,
	image360FilesArray,
	videoFilesArray,
	video360FilesArray,
	audioFilesArray,
	model3dFilesArray,
	fileType,
	onError,
	onCompletion,
	onProgress
}) => {
	if (!zwClient.zml) return await null;
	const res: (IFileImage | IFileStreamingVideo | IFileAudio | IFileModel3D | IFileImage360 | IFileVideo360)[] = [];    
    const workspaceId = store.getState().userReducer.project.accountId;
    
    const imageData = await zwClient.zml.uploadImages(imageFilesArray, (perc, progressData) => {
		uploadResponseHandler({
			onProgress,
			onCompletion,
			onError,
			progressData,
			perc: perc ?? 0,
			fileInfo: uploadIndexToFileDict[`image_${progressData.index}`],
			fileType,
		});
	}, workspaceId);
	res.push(...imageData);
	
	const image360Data = await zwClient.zml.uploadImages360(image360FilesArray, (perc, progressData) => {
		uploadResponseHandler({
			onProgress,
			onCompletion,
			onError,
			progressData,
			perc: perc ?? 0,
			fileInfo: uploadIndexToFileDict[`image360_${progressData.index}`],
			fileType,
		});
	}, workspaceId);
	res.push(...image360Data);
	const videoData = await zwClient.zml.uploadVideos(videoFilesArray, (perc, progressData) => {
		uploadResponseHandler({
			onProgress,
			onCompletion,
			onError,
			progressData,
			perc: perc ?? 0,
			fileInfo: uploadIndexToFileDict[`video_${progressData.index}`],
			fileType,
		});
	}, workspaceId);
	res.push(...videoData);
	const video360Data = await zwClient.zml.upload360Videos(video360FilesArray, (perc, progressData) => {
		uploadResponseHandler({
			onProgress,
			onCompletion,
			onError,
			progressData,
			perc: perc ?? 0,
			fileInfo: uploadIndexToFileDict[`video360_${progressData.index}`],
			fileType,
		});
	}, workspaceId);
	res.push(...video360Data);
	const audioData = await zwClient.zml.uploadAudio(audioFilesArray, (perc, progressData) => {
		uploadResponseHandler({
			onProgress,
			onCompletion,
			onError,
			progressData,
			perc: perc ?? 0,
			fileInfo: uploadIndexToFileDict[`audio_${progressData.index}`],
			fileType,
		});
	}, workspaceId);
	res.push(...audioData);
	const modelData = await zwClient.zml.upload3dModels(model3dFilesArray, (perc, progressData) => {
		uploadResponseHandler({
			onProgress,
			onCompletion,
			onError,
			progressData,
			perc: perc ?? 0,
			fileInfo: uploadIndexToFileDict[`model3d_${progressData.index}`],
			fileType,
		});
	}, workspaceId);
	res.push(...modelData)
	return res;
};

export const uploadFilesOfMultipleTypes: IUploadFilesOfMultipleTypes = async ({
	files, allowedFileTypes: allowedFiles, fileTypes, dispatch, onError, onCompletion, onProgress, onDrop, is360Content
}) => {
	const res: IUploadResponseFileTypes = [];
	for (let i = 0; i < fileTypes.length; i++) {
		const r = await uploadFilesOfType({files, allowedFileTypes: allowedFiles[i], fileType: fileTypes[i], dispatch, onError, onCompletion, onProgress, onDrop, is360Content})
		if (r) res.push(...r)
	}
	return res ?? null;
}

export const uploadFilesOfType: IUploadFilesOfType = async ({ files, allowedFileTypes, fileType, dispatch, onError, onCompletion, onProgress, onDrop, is360Content }) => {
	const onSetCurrentUploadsAction = onSetCurrentUploads;
	// const onAddToLoadedMediaAction = onAddToLoadedMedia;
	const _onError = (e: IOnErrorData) => {
		onErrorHandler(e, onSetCurrentUploadsAction, dispatch);
		onError?.(e);
	};
	const _onProgress = (p: IOnProgressData) => {
		onProgressHandler(p, onSetCurrentUploadsAction, dispatch);
		onProgress?.(p);
	};
	const _onDrop = (fileDict: IUploadIndexToFileDict) => {
		onSubmitHandler(fileDict, onSetCurrentUploadsAction, dispatch);
		onDrop?.(fileDict);
	};
	const _onCompletion = async (c: IOnCompletionData) => {
		await onCompletionHandler(c, onSetCurrentUploadsAction, fileType, dispatch);
		onCompletion?.(c);
	};

	const { uploadIndexToFileDict, imageFilesArray, image360FilesArray, videoFilesArray, video360FilesArray, audioFilesArray, model3dFilesArray } = createUploadIndexToFileDict({
		files,
		allowedFileTypes,
		fileType,
		dispatch,
		is360Content,
		onError: _onError,
		onProgress: _onProgress
	});

	_onDrop?.(uploadIndexToFileDict);

	return await uploadMedia({
		uploadIndexToFileDict, 
		imageFilesArray,
		image360FilesArray,
		videoFilesArray, 
		video360FilesArray,
		audioFilesArray, 
		model3dFilesArray, 
		fileType, 
		onError: _onError, 
		onCompletion: _onCompletion, 
		onProgress: _onProgress
	});
};

export const isValidModelFile = (filename: string) => {
	return (
		filename.endsWith('.gltf') ||
		filename.endsWith('.bin') ||
		filename.endsWith('.png') ||
		filename.endsWith('.jpg') ||
		filename.endsWith('.jpeg') ||
		filename.endsWith('.zip') ||
		filename.endsWith('.glb')
	);
};
export const getFileFromFileEntry = async (fileEntry: FileSystemFileEntry): Promise<File> => {
	try {
		return await new Promise((resolve, reject) => fileEntry.file(resolve, reject));
	} catch (err) {
		return new Promise((resolve, reject) => reject(err));
	}
};

// Takes output of webkitGetAsEntry() and a File[] and fills up the File[] with the files from webkitGetAsEntry() recursively
// Prefix is the original directory name, which isn't known by the GLTF paths, so must be removed
export const scanFiles = async (item: FileSystemDirectoryEntry | FileSystemFileEntry | FileSystemEntry, container: File[], prefix: string): Promise<boolean> => {
	return new Promise((resolve) => {
		const scanFilesAsyncFn = async () => {
			// If item is a file, add it to the container array and resolve
			if (item.isFile) {
				if (isValidModelFile(item.name)) {
					const retFile = await getFileFromFileEntry(item as FileSystemFileEntry);
					const dirFile = new File([retFile], item.fullPath.replace(prefix, ''));
					container.push(dirFile);
				}
				resolve(true);
			}

			// If item is a directory, recursively process it's contents
			if (item.isDirectory) {
				const directoryReader = (item as FileSystemDirectoryEntry).createReader();
				await new Promise<boolean>((resolve) => {
					directoryReader.readEntries(async (directoryEntries) => {
						const retArr = [] as boolean[];
						for (let i = 0; i < directoryEntries.length; i++) {
							if (item.name !== 'textures' && !prefix.includes(item.name)) prefix += item.name + '/';
							retArr.push(await scanFiles(directoryEntries[i], container, prefix));
						}
						await Promise.all(retArr);
						resolve(true);
					});
				});
				resolve(true);
			}
		};
		scanFilesAsyncFn();
	});
};

export const loadDirectoryFiles = async (items: DataTransferItemList) => {
	// Handle directory upload
	const directoryFiles: File[] = [];
	if (!items) return directoryFiles;
	const directoriesOnly: FileSystemEntry[] = [];
	for (let i = 0; i < items.length; i++) {
		const entry = items[i].webkitGetAsEntry();
		if (entry && entry.isDirectory) {
			directoriesOnly.push(entry);
		}
	}

	// Process directories
	await new Promise<boolean>(async (resolve) => {
		for (let i = 0; i < directoriesOnly.length; i++) {
			const entry = directoriesOnly[i];
			const prefix = `/${entry.name}/`;
			await scanFiles(entry, directoryFiles, prefix);
		}
		resolve(true);
	});
	return directoryFiles;
};
